import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between gap-3 p-4 hover:bg-gray-50/40 sm:flex-row sm:items-center" }
const _hoisted_2 = { class: "min-w-0 flex-1" }
const _hoisted_3 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_4 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_5 = ["aria-expanded", "aria-controls", "aria-label"]
const _hoisted_6 = {
  key: 0,
  class: "mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500"
}
const _hoisted_7 = { class: "mr-1 font-medium text-gray-700" }
const _hoisted_8 = ["id"]
const _hoisted_9 = { class: "flex items-center justify-between" }
const _hoisted_10 = { class: "font-medium text-gray-700" }
const _hoisted_11 = ["aria-label"]
const _hoisted_12 = { class: "flex shrink-0 items-center gap-3" }

import { computed, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    CheckCircleIcon,
    InformationCircleIcon,
    MinusCircleIcon,
    XIcon,
} from '@heroicons/vue/solid';
import { MetaPhoneNumber, MetaWhatsAppNumberPolicyPhone } from '@/types';
import PhoneNumberEligibilityState from './PhoneNumberEligibilityState.vue';
import { isPhoneEnabled } from '@/utils/whatsappMeta';


export default /*@__PURE__*/_defineComponent({
  __name: 'WhatsAppMetaPhoneNumberListItem',
  props: {
    phone: { type: Object as PropType<MetaPhoneNumber>, required: true },
    policyPhone: { type: Object as PropType<MetaWhatsAppNumberPolicyPhone | null>, default: null },
    canEnable: { type: Boolean, default: false },
},
  emits: ["activate", "approval"],
  setup(__props) {

const props = __props;



const { locale, t } = useI18n();
const detailsOpen = ref(false);

const isEnabled = computed(() => isPhoneEnabled(props.policyPhone));
const axes = computed(() => [
    {
        owner: t('whatsappMeta.phoneAxis.meta'),
        ok: props.policyPhone?.isActive === true,
        value: t(
            props.policyPhone?.isActive
                ? 'whatsappMeta.phoneAxis.registered'
                : 'whatsappMeta.phoneAxis.notRegistered',
        ),
    },
    {
        owner: t('whatsappMeta.phoneAxis.mobcall'),
        ok: isEnabled.value,
        value: t(
            isEnabled.value
                ? 'whatsappMeta.phoneAxis.enabled'
                : 'whatsappMeta.phoneAxis.notEnabled',
        ),
    },
]);
const canActivate = computed(() => Boolean(props.policyPhone && !props.policyPhone.isActive));
const canEnablePhone = computed(() =>
    Boolean(props.policyPhone?.isActive && !isEnabled.value && props.canEnable),
);
const canDisable = computed(() => Boolean(props.policyPhone?.isActive && isEnabled.value));
const detailsId = computed(() => `phone-details-${props.phone.phoneNumberId}`);
const lastSyncedAt = computed(() => {
    if (!props.phone.lastSyncedAt) return t('whatsappMeta.phone.notSynchronized');
    return new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(props.phone.lastSyncedAt));
});

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(__props.phone.displayPhoneNumber ||
                        __props.phone.phoneNumberId ||
                        _unref(t)('whatsappMeta.phone.unidentifiedNumber')), 1),
        _createVNode(PhoneNumberEligibilityState, { phone: __props.policyPhone }, null, 8, ["phone"]),
        _createElementVNode("button", {
          type: "button",
          class: "focus-visible:ring-primary inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2",
          "aria-expanded": detailsOpen.value,
          "aria-controls": detailsId.value,
          "aria-label": _unref(t)('whatsappMeta.detail.showTechnicalDetails'),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (detailsOpen.value = !detailsOpen.value))
        }, [
          _createVNode(_unref(InformationCircleIcon), {
            class: "h-4 w-4",
            "aria-hidden": "true"
          })
        ], 8, _hoisted_5)
      ]),
      (__props.policyPhone)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(axes.value, (axis) => {
              return (_openBlock(), _createElementBlock("span", {
                key: axis.owner,
                class: "flex items-center"
              }, [
                (axis.ok)
                  ? (_openBlock(), _createBlock(_unref(CheckCircleIcon), {
                      key: 0,
                      class: "mr-1 h-4 w-4 text-emerald-500",
                      "aria-hidden": "true"
                    }))
                  : (_openBlock(), _createBlock(_unref(MinusCircleIcon), {
                      key: 1,
                      class: "mr-1 h-4 w-4 text-gray-400",
                      "aria-hidden": "true"
                    })),
                _createElementVNode("span", _hoisted_7, _toDisplayString(axis.owner) + ":", 1),
                _createTextVNode(" " + _toDisplayString(axis.value), 1)
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (detailsOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            id: detailsId.value,
            class: "mt-2.5 space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
          }, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)('whatsappMeta.detail.technicalDetails')), 1),
              _createElementVNode("button", {
                type: "button",
                class: "text-gray-400 hover:text-gray-600",
                "aria-label": _unref(t)('whatsappMeta.detail.closeTechnicalDetails'),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (detailsOpen.value = false))
              }, [
                _createVNode(_unref(XIcon), {
                  class: "h-3.5 w-3.5",
                  "aria-hidden": "true"
                })
              ], 8, _hoisted_11)
            ]),
            _createElementVNode("p", null, _toDisplayString(_unref(t)('whatsappMeta.detail.verifiedName')) + ": " + _toDisplayString(__props.phone.verifiedName || _unref(t)('whatsappMeta.phone.noVerifiedName')), 1),
            _createElementVNode("p", null, _toDisplayString(_unref(t)('whatsappMeta.detail.metaPhoneId')) + ": " + _toDisplayString(__props.phone.phoneNumberId || _unref(t)('whatsappMeta.phone.idUnavailable')), 1),
            _createElementVNode("p", null, _toDisplayString(_unref(t)('whatsappMeta.detail.registrationStatus')) + ": " + _toDisplayString(__props.policyPhone?.registrationStatus || __props.phone.registrationStatus), 1),
            _createElementVNode("p", null, _toDisplayString(_unref(t)('whatsappMeta.detail.lastSyncedAt')) + ": " + _toDisplayString(lastSyncedAt.value), 1)
          ], 8, _hoisted_8))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_12, [
      (canActivate.value && __props.phone.phoneNumberId)
        ? (_openBlock(), _createBlock(_component_UiButton, {
            key: 0,
            variant: "white",
            "text-variant": "primary",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('activate', __props.phone.phoneNumberId)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.phone.activateOnMeta')), 1)
            ]),
            _: 1
          }))
        : (canEnablePhone.value && __props.policyPhone)
          ? (_openBlock(), _createBlock(_component_UiButton, {
              key: 1,
              variant: "white",
              "text-variant": "primary",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('approval', __props.policyPhone, true)))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.phone.enable')), 1)
              ]),
              _: 1
            }))
          : (canDisable.value && __props.policyPhone)
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 2,
                variant: "white",
                "text-variant": "red-600",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('approval', __props.policyPhone, false)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('whatsappMeta.phone.disable')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
    ])
  ]))
}
}

})